'use client'

import type {
  ComponentProps,
  ElementType,
  MouseEvent,
  PropsWithChildren,
} from 'react'

import { Link } from '@/shared/i18n'
import { cn, createSlot } from '@/shared/lib'
import { Image } from '@/shared/ui'

type TitleProps = {
  className?: string
}

export const Title = ({
  children,
  className,
}: PropsWithChildren<TitleProps>) => {
  return (
    <h3 className={cn('typo-headline3 font-bold text-black', className)}>
      {children}
    </h3>
  )
}

export const List = ({ children }: PropsWithChildren) => {
  return <ul className="space-y-6">{children}</ul>
}

type ItemProps<E extends ElementType> = {
  as?: E | typeof Link
  closed?: boolean
  className?: string
  orientation?: 'vertical' | 'horizontal'
}

type ItemComponentProps<E extends ElementType> = ItemProps<E> &
  ComponentProps<E>

export const Item = <E extends ElementType>({
  as,
  closed = false,
  orientation = 'horizontal',
  className,
  children,
  ...restProps
}: PropsWithChildren<ItemComponentProps<E>>) => {
  const Component = as || Link

  const { slots } = createSlot(children, [
    'image',
    'category',
    'title',
    'subContent',
  ])

  const imageSize = (() => {
    if (orientation === 'horizontal') {
      return {
        width: 100,
        height: 100,
      }
    }

    return {
      width: 343,
      height: 116,
    }
  })()

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (closed) {
      event.preventDefault()
    }
  }

  return (
    <li>
      <Component
        className={cn(
          'flex',
          {
            'cursor-default': closed,
            'gap-4': orientation === 'horizontal',
            'flex-col gap-2 md:flex-row md:gap-6': orientation === 'vertical',
          },
          className,
        )}
        onClick={handleClick}
        {...restProps}
      >
        {slots.image && (
          <div
            className={cn('shrink-0', {
              'aspect-343/116 w-full min-w-0 md:w-[343px]':
                orientation === 'vertical',
            })}
          >
            <Image
              src={slots.image as string}
              alt={slots.title as string}
              width={imageSize.width}
              height={imageSize.height}
              unoptimized
              placeholder
              className={cn({
                'opacity-50': closed,
                'min-w-25 rounded-sm': orientation === 'horizontal',
                'rounded-lg': orientation === 'vertical',
              })}
              style={{
                width: '100%',
                height: '100%',
              }}
              imageStyle={{
                width: '100%',
              }}
            />
          </div>
        )}
        <div
          className={cn('flex flex-col justify-center space-y-1', {
            'md:min-w-0 md:flex-1': orientation === 'vertical',
          })}
        >
          {slots.category && (
            <p className="typo-caption2 font-semibold text-purple-500">
              {slots.category}
            </p>
          )}
          {slots.title && (
            <h4
              className={cn('typo-body1 font-semibold text-gray-900', {
                'text-gray-400': closed,
                'line-clamp-2 font-medium': orientation === 'horizontal',
                'line-clamp-1': orientation === 'vertical',
              })}
            >
              {slots.title}
            </h4>
          )}
          {slots.subContent && (
            <p
              className={cn('typo-label2 font-normal text-gray-500', {
                'text-gray-400': closed,
                'line-clamp-1': orientation === 'horizontal',
                'line-clamp-2': orientation === 'vertical',
              })}
            >
              {slots.subContent}
            </p>
          )}
        </div>
      </Component>
    </li>
  )
}

export const Empty = ({ children }: PropsWithChildren) => {
  return (
    <div className="typo-label1 flex h-38 items-center justify-center text-gray-400">
      {children}
    </div>
  )
}
